import get from "lodash/get";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getStripeDate } from "../../../../utils/format-stripe-date";
import { formatNumber } from "../../../../utils/format-sub";
import SortableHeader from "./SortableHeader";

const STATUSES = ["paid", "pending", "in_transit", "failed", "canceled"];

const COLUMNS = [
  { key: "amount", labelKey: "income.amount", className: "text-left!" },
  { key: "at", labelKey: "income.created" },
  { key: "arrivalDate", labelKey: "income.arrival" },
  { key: "status", labelKey: "income.payouts.status" },
];

const fmt = (stamp) => {
  if (!stamp) return "—";
  const { day, month, year } = getStripeDate(stamp);
  return `${month} ${day}, ${year}`;
};

/**
 * The Payouts tab's table (§5.10's "non-optional" pagination — finding 2 of
 * the B2 final review). Same shape as TransactionsTable: fe-payouts returns
 * the whole window in one page, so the status filter and the column sort
 * are client-side; server-rendered props seed local state, a resync effect
 * keeps that state honest when the loader reruns (a range-selector change,
 * a context switch), and Load more survives only for an account big enough
 * to overflow that page.
 */
const PayoutsTable = ({
  payouts: initialPayouts = [],
  hasMore: initialHasMore = false,
  nextStartingAfter: initialCursor = null,
  total = 0,
  currency = "usd",
  selector,
  context,
  organizationUser,
  api,
}) => {
  const { t } = useTranslation("coaching-portal");
  const [payouts, setPayouts] = useState(initialPayouts);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [cursor, setCursor] = useState(initialCursor);
  const [loadingMore, setLoadingMore] = useState(false);
  const [status, setStatus] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "at",
    direction: "descending",
  });

  useEffect(() => {
    setPayouts(initialPayouts);
    setHasMore(initialHasMore);
    setCursor(initialCursor);
  }, [initialPayouts, initialHasMore, initialCursor]);

  const loadMore = async () => {
    if (cursor == null || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await api("fe-payouts", {
        query: {
          selector,
          context,
          startingAfter: cursor,
          ...(organizationUser && { organizationUser }),
        },
      });
      const data = await res.json();
      setPayouts((prev) => [...prev, ...(data.payouts || [])]);
      setHasMore(data.hasMore);
      setCursor(data.nextStartingAfter);
    } finally {
      setLoadingMore(false);
    }
  };

  const visiblePayouts = useMemo(
    () => (status ? payouts.filter((p) => p.status === status) : payouts),
    [payouts, status]
  );

  const sortedPayouts = useMemo(() => {
    const sorted = [...visiblePayouts];
    sorted.sort((a, b) => {
      const aVal = get(a, sortConfig.key);
      const bVal = get(b, sortConfig.key);
      if (aVal === bVal) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;
      const cmp =
        typeof aVal === "string" && typeof bVal === "string"
          ? aVal.localeCompare(bVal)
          : aVal < bVal
            ? -1
            : 1;
      return sortConfig.direction === "ascending" ? cmp : -cmp;
    });
    return sorted;
  }, [visiblePayouts, sortConfig]);

  const handleSort = (key, direction) => setSortConfig({ key, direction });

  return (
    <>
      <div className="payouts-table-filters">
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">{t("income.payouts.allStatuses")}</option>
          {STATUSES.map((value) => (
            <option key={value} value={value}>
              {t(`income.payouts.statuses.${value}`)}
            </option>
          ))}
        </select>
        <p className="payouts-table__count">
          {t("income.payouts.count", {
            shown: sortedPayouts.length,
            total: status ? payouts.length : total,
          })}
        </p>
      </div>
      <hr className="mt-3 mb-0!" />
      {/* biome-ignore lint/a11y/useValidAriaRole: report-table used for structure */}
      <table role="report-table" className="payouts-table">
        {/* biome-ignore lint/a11y/useValidAriaRole: table-header used for structure */}
        <thead role="table-header">
          <tr>
            {COLUMNS.map((column) => (
              <SortableHeader
                key={column.key}
                column={column}
                sortConfig={sortConfig}
                onSort={handleSort}
                t={t}
                className={column.className}
              />
            ))}
          </tr>
        </thead>
        {sortedPayouts.length > 0 ? (
          <tbody>
            {sortedPayouts.map((payout) => (
              <tr
                key={payout.id}
                data-failed={payout.status === "failed" ? "true" : undefined}
              >
                <td className="amount text-left!">
                  {formatNumber(payout.amount, payout.currency || currency)}
                </td>
                <td className="whitespace-nowrap">{fmt(payout.at)}</td>
                <td className="whitespace-nowrap">{fmt(payout.arrivalDate)}</td>
                <td>
                  {t(`income.payouts.statuses.${payout.status}`, {
                    defaultValue: payout.status,
                  })}
                  {payout.status === "failed" && payout.failureCode
                    ? ` — ${payout.failureCode}`
                    : ""}
                </td>
              </tr>
            ))}
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td colSpan={COLUMNS.length}>
                <div className="body-table-empty">
                  <p>
                    {payouts.length
                      ? t("income.payouts.noResults")
                      : t("income.payouts.empty")}
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        )}
      </table>
      {hasMore && (
        <button
          type="button"
          data-button-type="secondary"
          className="payouts-table__load-more"
          onClick={loadMore}
          disabled={loadingMore}
        >
          {loadingMore
            ? t("income.payouts.loading")
            : t("income.payouts.loadMore")}
        </button>
      )}
    </>
  );
};

export default PayoutsTable;
